// @givmo/org-console — the sidebar rule, ONE pure function for every page (shell contract v3, T6).
//
// ══ ONE LAYOUT, CHOSEN NOWHERE ═════════════════════════════════════════════════════════════════
//
// Every authenticated console page wears the same frame — the sidebar (a drawer below `lg`) and
// the top bar. What differs per route is the sidebar's CONTENT, and that is a content question
// answered here rather than a layout question answered by a mode switch: a module page's rows are
// the module's declared nav plus the shell's own Team row plus the Organization section; an org
// page's rows are its family's; a page that declares nothing gets the Organization section.
//
// ══ AN ORGANIZATION-SECTION PAGE KEEPS THE MODULE'S FRAME (the 2026-09-04 walkthrough ruling) ═══
//
// An Organization row's page (a Profile, a Wallet) is opened FROM a module, and losing that module's
// rows there reads as having changed applications. So such a page wears the frame of the module the
// host names — `frameModule`, the last-module hint on this device, first; then `frameFallback`, the
// class default — exactly as the module branch renders it, with the page's own Organization row
// active and its label the title. A candidate frames ONLY when that module is mounted here AND this
// person can open it (they hold it, and its landing row resolves to a link under the row rule
// below); a stale hint — a module since unmounted, switched off or lost — falls to the class
// default, and when neither applies the page keeps, as every other undeclared page does, the
// Organization section alone — the honest minimum, never a frame of greyed rows. A declared page may
// ask for the same frame (`PageNav.moduleFrame` — the gear's preferences page, the same day's
// amendment): the frame first, its own sections after it, its own word as the title, and its own
// sections alone when no candidate applies.
//
// ══ A ROW MAY BORROW ONE PAGE OF ANOTHER MODULE — OR AN ORGANIZATION ROW (D-CPO-40 (3)) ═════════
//
// A module declares, among its own rows, a REFERENCE to a page another module (or the Organization
// section) already puts in its sidebar (`BorrowedRowDeclaration`). The row resolves exactly as the
// referenced row would — its address and its permission string are never re-declared — and, when
// FOLLOWED, the page wears the BORROWER's frame with that row lit and the sidebar otherwise
// unchanged; the page-only breadcrumb reads the row's word. The frame rides the host's router state
// on the sidebar's own navigations (`borrowedFrom`) and, inside the borrowed subtree, the last-module
// hint (`frameModule`) — never the class default: a direct load, a bookmark or a module switch lands
// in the destination's own frame. A borrow is embed-or-nothing: a reference to an unmounted module,
// a cross-origin page, an unknown row or a LINK row into a third module is OMITTED, and the host's
// rail names it. The Organization
// section omits a row a module borrows into its own section, in that module's frame (a page appears
// once per sidebar). The shell's reserved `<path>/team` and `<path>/sign-in` are never borrowed.
//
// ══ A ROW GATES ON THE PERMISSION STRING ITS PAGE REQUIRES — NEVER ON A TILE VERDICT (R8) ══════
//
// Every declared row names the ONE permission string its page's service requires — the same string
// the service's decorator names, declared once here and enforced once there, so a row and its page
// can never disagree. The shell composes:
//
//   * the standing NOT REPORTED (`permissions === null`) ⇒ every declared row is a plain link, the
//     tile posture mirrored;
//   * the destination's HOME APP is `off` or `setting-up` ⇒ a greyed row carrying THAT reason as
//     its status line ("Compliance is off", "Books is setting up") and NO request affordance — the
//     mockup pass caught rows leading live into a closed door;
//   * a module's OWN row, drawn in that module's sidebar (its home app is the module that declares
//     it), for a person who does NOT hold the module ⇒ that module's REQUEST row, whatever strings
//     they hold (Org Machinery OM-32 — the module's standing decides before the string: a page read
//     on a string every admin holds still sits behind its module's gate, and a live row there led
//     onto the lock). A cross-module row and a BORROWED row keep the rules below;
//   * the string is held AND the home app is on ⇒ a link;
//   * the string is missing and the home app is one the person does NOT hold ⇒ a greyed REQUEST
//     row, whose click opens the request dialog asking for a role in that module;
//   * the string is missing and the home app IS held (or the page is no module's) ⇒ the row is
//     ABSENT (Org Machinery OM-29 — Jonathan, 2026-09-28 for the Team row and 2026-09-29 for every
//     other: a row the caller cannot use is absent, not a locked "You do not have access" row; a
//     request row stays, because it still does something);
//   * the home app is `absent` for this class ⇒ the row is omitted.
//
// Cross-module rows are the ORDINARY case: a Board Member's catalog carries the compliance
// calendar's read string, so that row is LIVE without holding Compliance — while a Compliance page
// whose string the Board catalog does not carry is a request row, or absent for a Compliance holder.
// The mechanism is the catalog union the resolver already performs; this file only reads the
// effective set.
//
// ⚠ THE TILE VERDICT NEVER ENTERS THIS FILE AS A PERMISSION TEST. The route gate is the verdict's
// only reader. The one non-permission input here is the home app's ON/OFF standing — read directly
// off the standing row (`orgEnabled` / `activationState`), never through the verdict — and the one
// affordance choice reads `held` to pick request-vs-absent. `sidebar.test.ts` grep-pins that
// `verdictFor` / `moduleVerdict` are not imported here.

import { isMountedInThisBuild, moduleHref, type ModuleRoute } from './module-routes';
import { titleizeSlug } from './org-console-config';
import type { ModuleStandings } from './module-verdict';
import type { ModuleSettings, OrganizationSettingsCard } from './settings-declarations';
import { approveAccessRequestsPermission } from './standing-read';
import { TEAM_COPY } from './org-team-copy';

// ── The declarations a module (or a page) supplies — DATA ───────────────────

/**
 * Where a sidebar row goes. Three kinds; a section header is not a row.
 *
 *   (a) `page`   — the module's OWN page, a path under the module (or, for a non-module page's
 *                  declaration, any path in this console);
 *   (b) `module` — ANOTHER module's page, `{slug, subpath?}`, resolved through the ONE resolver:
 *                  in-app when that module is mounted here, cross-origin otherwise (the `subpath`
 *                  appended to the operator-set URL verbatim), OMITTED when it has no address;
 *   (c) `org`    — an organization page (the Organization section's class rows).
 */
export type SidebarDestination =
  | { kind: 'page'; path: string }
  | { kind: 'module'; slug: string; subpath?: string }
  | { kind: 'org'; path: string };

/**
 * A COUNT a module supplies for one of its OWN declared rows (Org Machinery OM-30) — the Donations
 * module's Approvals row and its pending-match count first (D-EP-48's parity row 5: without it the
 * mount loses the portal's badge).
 *
 * The MODULE supplies the number; the SHELL owns the look (the same badge the Team row wears) and the
 * honesty: nothing renders while the count is zero, unknown, or not a finite number, and nothing
 * renders on a row that is not a live link (a request or statement row carries no count). The count
 * is a React hook the nav calls ONLY while the row renders live, so its read runs where the row is on
 * screen — inside the frame of the module that declared it, or of a module that borrows the row
 * (OM-18) — and nowhere else. It must be stable (a module-level function), exactly as a row's `icon`
 * is.
 */
export interface RowCountBadge {
  /** The count, or `null` / `undefined` while it is unknown (a read in flight, or one that failed). */
  useCount: () => number | null | undefined;
}

/**
 * Whether the page of one of a module's OWN declared rows is SERVED here (Org Machinery OM-99): a
 * page the backend may withhold, the module answering from its own read of it. The count's sibling
 * (OM-30) in shape and lifecycle, with the same honesty turned on the row itself: the shell draws the
 * row only on a settled `true`; `false`, and `null` / `undefined` while the answer is unknown (a read
 * in flight, or one that failed), draw nothing at all. A React hook the nav calls ONLY while the row
 * renders live — inside the frame of the module that declared it, or of a module that borrows the
 * row (OM-18), and nowhere else — and stable (a module-level function), exactly as a row's `icon` is.
 *
 * A row that declares it is never a request or a statement row: where the rule would draw one (its
 * home app off or setting up, or a person who does not hold the module), the row is OMITTED and its
 * hook never asked, so a page its module may withhold is named nowhere, not even as a door or a
 * sentence about access.
 *
 * What it does not move: the rule still computes the live row, so the lit row, the title and the
 * frame are the declared ones; a withheld row draws no list item, so the list stays evenly spaced and
 * the Settings row stays last (OM-22, OM-23). A direct load of the row's page is the PAGE's to answer
 * — its own not-available state, never a blank shell.
 *
 * Its limit: the nav draws a section before any hook answers, so a section whose every row is
 * withheld still draws its group (its caption and gap on the shown arm, an empty item in the column on
 * the hidden arm). A module puts it only on a row whose section still draws a row whenever it would
 * draw this one — in practice a section-mate on the same string and home app. The shell dropping
 * such a group is Org Machinery's OM-109.
 */
export interface RowServed {
  /** `true` once the page is known served; `false` once known not; `null` / `undefined` unknown. */
  useServed: () => boolean | null | undefined;
}

/** One declared row. `icon` is whatever the host renders — this package holds no glyph set. */
export interface NavRowDeclaration<Icon = unknown> {
  /** Stable, unique within the sidebar — the row's testid derives from it. */
  id: string;
  label: string;
  icon?: Icon;
  destination: SidebarDestination;
  /**
   * The ONE permission string the row's page requires — the string its service's decorator names.
   * An empty string means the page requires nothing beyond being an admin here (a plain link).
   */
  requires: string;
  /** A count the module supplies for this row, drawn by the shell (OM-30). See {@link RowCountBadge}. */
  countBadge?: RowCountBadge;
  /**
   * Whether this row's page is served here, a hook the module supplies; the shell draws the row only
   * on a settled `true` (OM-99). Absent, the row is drawn as it always was. See {@link RowServed}.
   */
  served?: RowServed;
  /**
   * Console paths of pages this row's page OWNS without sitting beneath its address — a workspace
   * reached from the page, such as the Board Minute book's import workspace (D-CBG-9). The row is lit
   * on each path and every address beneath it by segment, exactly as on its own, and the longest
   * match across every row still wins. It decides which row is LIT, never which frame a page wears.
   */
  alsoActiveUnder?: readonly string[];
}

/**
 * ONE page another module — or the Organization section — already declares as a row: that module's
 * slug plus the id of its declared row, or the id of an Organization-section row (an org-level page).
 */
export type BorrowedPageRef = { module: string; row: string } | { organization: string };

/**
 * A row that BORROWS one page another module (or the Organization section) puts in its own sidebar
 * (D-CPO-40 (3) — the shell contract v3 amendment). A REFERENCE to that declared row, never a copy
 * of its address or its permission string: both stay the referenced row's, so "the permission is the
 * destination page's own" is a mechanism the borrower cannot drift from. Followed, the page wears the
 * BORROWER's frame with this row lit (`borrowedFrom`, below); a direct load wears its own. What is
 * borrowed is that row's whole subtree — the page and every address beneath it by segment — except
 * the shell's reserved pages. The borrower may override `label` and `icon` only.
 */
export interface BorrowedRowDeclaration<Icon = unknown> {
  /** THIS module's row id — `menu-<id>` in ITS frame. Stable, unique within the sidebar. */
  id: string;
  borrow: BorrowedPageRef;
  /** Defaults to the referenced row's label. */
  label?: string;
  /** Defaults to the referenced row's icon. */
  icon?: Icon;
}

/** A section's row: the declaring module's own, or a borrowed one. */
export type SectionRowDeclaration<Icon = unknown> =
  | NavRowDeclaration<Icon>
  | BorrowedRowDeclaration<Icon>;

/** Whether a section row is a BORROW — a reference, with no destination or string of its own. */
export function isBorrowedRow<Icon>(
  row: SectionRowDeclaration<Icon>,
): row is BorrowedRowDeclaration<Icon> {
  return 'borrow' in row;
}

/** Whether a section row is the declaring module's OWN — it carries a destination and a string. */
export function isNavRow<Icon>(row: SectionRowDeclaration<Icon>): row is NavRowDeclaration<Icon> {
  return !isBorrowedRow(row);
}

/** A section header (kind d) with its rows. */
export interface NavSectionDeclaration<Icon = unknown> {
  /** Stable, unique within the sidebar. */
  id: string;
  label: string;
  rows: readonly SectionRowDeclaration<Icon>[];
}

/**
 * A module's declared nav — registered by its package beside its route tree, handed in by the host.
 *
 * `teamSection` names which declared section the shell's own Team row joins (the first, by
 * default). `includeOrganizationSection` defaults to `true` — the Organization section is every
 * module page's floor unless the module says otherwise.
 */
export interface ModuleNav<Icon = unknown> {
  slug: string;
  sections: readonly NavSectionDeclaration<Icon>[];
  includeOrganizationSection?: boolean;
  teamSection?: string;
  /**
   * ONE caption over the Organization section, in this module's own frame (Org Machinery OM-69, from
   * Board's D-CBG-9 amendment: Jonathan, 2026-10-22 21:10 PDT, "just make it flat except the Admin
   * group"). Opt-in, and the words are the declaration's, never the shell's: absent (or blank), the
   * frame renders exactly as it does without it.
   *
   * Declared, the frame's Organization section becomes its ADMIN group: the shell's Team row joins
   * it, FIRST (`teamSection` is not read), then the Organization rows the page carries, and the trailing
   * Settings row ends it (OM-22). The frame always carries that section: `includeOrganizationSection:
   * false` is not read either. The group draws this caption with OM-23's 16px group gap between it and
   * a group above it;
   * the module's own sections keep the frame's own caption answer (OM-27: a flat module's list stays
   * uncaptioned, its section's label the list's accessible name). The section's id, label and rows are
   * otherwise the Organization section's, so the bar still reads "Organization" on an Organization page.
   *
   * PRECEDENCE: it is drawn in THIS module's frame — on every page that wears it — whatever OM-21's host
   * caption declaration (`OrgSidebarNav`'s `captions`) says, and nowhere else: no other module's frame,
   * not the Admin Console family, not the Organization-alone floor (`moduleSections` puts it on the
   * frame's Organization section only; the nav draws a section's own caption on either arm).
   */
  adminCaption?: string;
}

/**
 * A NON-module page's declaration — the apps page, the organization's own pages, the preferences
 * page, the not-found page — matched by exact path or by prefix (`/org` matches `/org/team-access`).
 * A page that declares nothing gets the Organization section by the default rule (T6.4).
 */
export interface PageNav<Icon = unknown> {
  path: string;
  /** Match every path underneath `path` too. Off by default (exact match). */
  prefix?: boolean;
  title: string;
  /**
   * The APP this page family is — the top bar's word on each of its pages (Org Machinery OM-26):
   * "Admin Console" on the `/org` family. Absent on a page that is no app's own (a class page, the
   * apps statement page, the settings page), whose bar names the page itself or the frame it wears.
   */
  app?: string;
  sections: readonly NavSectionDeclaration<Icon>[];
  includeOrganizationSection?: boolean;
  /**
   * Wear the module FRAME (the 2026-09-04 walkthrough amendment): the module `frameModule` — else
   * `frameFallback` — names, rendered exactly as its own pages render it — its sections, the Team
   * row, the Organization section — FIRST, then this page's own sections after it. The same guard
   * as an Organization page's: a candidate must be mounted here and openable by this person, else
   * the page renders its own sections alone, as before. The title stays this page's own word. Off
   * by default; the Admin Console family and the apps page never set it.
   */
  moduleFrame?: boolean;
}

export interface SidebarDeclarations<Icon = unknown> {
  /** Every mounted module's nav, keyed by slug. A mounted module with no entry still gets its Team row. */
  modules: readonly ModuleNav<Icon>[];
  /** The Organization section's class rows — data. Empty for a class that has none. */
  organization: readonly NavRowDeclaration<Icon>[];
  /** The non-module pages' own declarations. */
  pages?: readonly PageNav<Icon>[];
  /** The glyph the shell's own Team row renders — the one row this package declares, not a module. */
  teamIcon?: Icon;
  /** The Organization section's label. Defaults to "Organization". */
  organizationLabel?: string;
  /**
   * The settings each mounted module DECLARES for the composed settings page (OM-17 seam B),
   * beside its `ModuleNav`. The sidebar rule never reads these; the settings composer does.
   */
  moduleSettings?: readonly ModuleSettings[];
  /** The Organization section's class cards on the composed settings page (OM-17 design 3). */
  organizationSettings?: readonly OrganizationSettingsCard[];
}

// ── What the shell renders — the resolved rows ───────────────────────────────

/** How a resolved row renders. See the header for the rule. */
export type SidebarRowState = 'link' | 'request' | 'statement';

export interface SidebarRow<Icon = unknown> {
  id: string;
  label: string;
  icon?: Icon;
  /** The real href for a `link` row; `null` for a greyed row (which navigates nowhere). */
  href: string | null;
  /** A `link` whose destination is on another origin — the host takes the document hop. */
  external: boolean;
  state: SidebarRowState;
  /**
   * For a greyed row: WHY. A string-missing row carries the no-access sentence; a home-app-off or
   * setting-up row carries that app's reason ("Compliance is off"). Rendered as the status line
   * and as the accessible name's second half.
   */
  reason?: string;
  /** For a `request` row: the module the string belongs to — the dialog asks for a role in it. */
  requestModule?: string;
  requires: string;
  active: boolean;
  /** The declaration's `alsoActiveUnder`, carried for `markActive` (a live row only). */
  alsoActiveUnder?: readonly string[];
  badge?: number;
  /**
   * The module-supplied count (OM-30), carried on a LIVE row only — the nav calls it and draws the
   * shell's badge. A row with it draws no other badge.
   */
  countBadge?: RowCountBadge;
  /**
   * Whether the page is served (OM-99), carried on a LIVE row only — the nav asks it and draws the
   * row only on a settled `true`.
   */
  served?: RowServed;
  /** `menu-<id>` for a live row, `menu-link-<id>` for a greyed one — the contract's two families. */
  testId: string;
  /** Set on a row that BORROWS another module's (or the Organization section's) page. */
  borrowed?: true;
}

export interface SidebarSection<Icon = unknown> {
  id: string;
  label: string;
  rows: SidebarRow<Icon>[];
  /**
   * A caption this section DRAWS whatever the frame's caption answer or the host's override says: the
   * module's `adminCaption` (OM-69), set on the frame's Organization section alone. Absent everywhere
   * else, where a section's caption is its `label`, drawn only on the shown arm.
   */
  caption?: string;
}

/**
 * Whether a sidebar's section captions are DRAWN — `OrgSidebarNav`'s rendering choice (its §Captions
 * says what each arm draws). Since Org Machinery OM-27 the FRAME decides, never a host-wide
 * declaration: `'shown'` in the frame of a module that declares more than one section, `'hidden'`
 * everywhere else — a one-section module (every module until a four-section Donations module), the
 * Admin Console family, an unframed page — exactly as OM-21/OM-23 left them.
 */
export type SidebarCaptions = 'shown' | 'hidden';

export interface Sidebar<Icon = unknown> {
  /** The page's own title — the active row's word, else the family's own name. */
  title: string;
  /**
   * The APP the page is in — the top bar's one word (Org Machinery OM-26, amending D1: the bar
   * names the module, not the page; the words ruled 2026-09-29). The module's label on its own
   * pages and on a page another module's row BORROWED into its frame; a declared family's own name
   * (`PageNav.app` — "Admin Console" on the `/org` family); the Organization section's label
   * ("Organization") on every Organization page, whatever frame it wears; on the settings page,
   * the module whose frame it wears — the module it was opened from ("Board") — else the app of the
   * page it was opened from. Absent where no app frames the page — a class page of its own, the
   * settings page with no origin and no frame, the not-found surface — and there the bar names the
   * page (`title`).
   */
  app?: string;
  sections: SidebarSection<Icon>[];
  /**
   * The module whose FRAME rendered: the current module on its own pages, the frame an Organization
   * page or a flagged page wears, the BORROWER on a borrowed page. Absent on the Admin Console family
   * and on the Organization-alone floor. The host carries it on the sidebar's own navigations (as
   * `SidebarInputs.borrowedFrom`) and writes it as the last-module hint on module pages.
   */
  frame?: string;
  /**
   * Whether this sidebar's section captions are drawn — the FRAME's choice (Org Machinery OM-27;
   * {@link SidebarCaptions}). `sidebarFor` always answers it; the nav draws what it says unless a
   * host overrides.
   */
  captions?: SidebarCaptions;
}

export interface SidebarInputs<Icon = unknown> {
  pathname: string;
  /**
   * Every module this host knows, with `label` for the reason lines. Rows with no address are
   * unmounted. A row flagged `adminConsole` (the ONE console table's management tile) is skipped:
   * its pages are declared under `pages`, never as a module's nav.
   */
  routes: readonly (ModuleRoute & { label?: string; adminConsole?: boolean })[];
  declarations: SidebarDeclarations<Icon>;
  /** The caller's per-module standing, or `null` when not reported. */
  standings: ModuleStandings | null;
  /** The caller's EFFECTIVE permission set, or `null` when not reported — the ONE authority input. */
  permissions: readonly string[] | null;
  /** The live origin the ONE resolver compares against. */
  origin: string;
  /** Per-module pending-request counts for the Team row's badge (an Owner's own module). */
  pendingByModule?: Readonly<Record<string, number>>;
  /** The Team row's label. Defaults to "Admin Team" (OM-100, `TEAM_COPY.adminTeam`). */
  teamLabel?: string;
  /**
   * The module whose FRAME an Organization-section page — or a `PageNav` flagged `moduleFrame` —
   * wears: the host's last-module hint on this device (`readLastModule`), `null` when there is
   * none. Tried FIRST, under the guard: the named module must be mounted here (a route with a path
   * AND an address, never the `adminConsole` row) and OPENABLE by this person — they hold it and its
   * landing row resolves to a `link`; otherwise `frameFallback` is tried, and when neither applies
   * the page renders as if no frame were named (the Organization section alone; a flagged page's
   * own sections alone).
   */
  frameModule?: string | null;
  /**
   * The frame's FALLBACK — the host's class default module — tried when `frameModule` is absent or
   * fails the guard (a stale hint: a module since unmounted, switched off or lost). The identical
   * guard; `null` or a slug that fails it leaves the page unframed.
   */
  frameFallback?: string | null;
  /**
   * The ADDRESS a `moduleFrame` page was opened from — the page the gear was pressed on (D-CPO-33,
   * the OM-9 build: "the sidebar framed by the gear's ORIGIN"). When given, the flagged page wears
   * THAT address's sidebar exactly as that address renders it — the Admin Console family from an
   * Admin Console page, a module's frame from one of its pages, an Organization page's frame from
   * it — with no row lit, the page's own sections after it, and the page's own title. Nothing is
   * appended: the origin's sidebar is the whole answer. The host reads it off the page's ADDRESS
   * (`?from=…`, Org Machinery OM-28), else an earlier build's history state, so it counts only
   * where it gives this person a place to stand (`originGivesAPlace`). Absent (a typed address),
   * the page's own address, or an origin that gives no place ⇒ the two-step rule above
   * (`frameModule`, then `frameFallback`), then the page's own sections and the Organization
   * section by its flag.
   */
  framePath?: string | null;
  /**
   * The frame the navigation that reached this page CARRIED — the host's router history state; on
   * the settings page, the frame its address names beside the origin (`&frame=…`) — tri-state: a slug (the module whose BORROWED row was followed; it frames this page iff it borrows
   * it, and is inert on every other page); `null` (EXPLICITLY none — a module switch: the
   * destination's own frame, and the hint is NOT consulted); absent (a navigation that carried
   * nothing — a module's own in-page link, a typed address — falls to `frameModule`, the hint, which
   * the host keeps equal to the frame inside a borrowed subtree). NEVER `frameFallback`: a borrowed
   * page has a frame of its own, so the class default is not a candidate for it.
   */
  borrowedFrom?: string | null;
}

/**
 * The sentence a greyed REQUEST row carries — pinned copy (D-CPO-31). A row the caller cannot use
 * in a module they hold is absent (Org Machinery OM-29), so no row states it without a door.
 */
export const SIDEBAR_NO_ACCESS_LINE = 'You do not have access';

// ── The rule ─────────────────────────────────────────────────────────────────

function isUnder(pathname: string, path: string): boolean {
  return pathname === path || pathname.startsWith(`${path}/`);
}

function normalizePath(path: string | undefined): string | undefined {
  const raw = (path ?? '').trim();
  if (!raw.startsWith('/')) return undefined;
  const trimmed = raw.replace(/\/+$/, '');
  return trimmed.length > 1 ? trimmed : undefined;
}

/**
 * Which mounted module a pathname is inside — segment boundary, longest match. A row flagged
 * `adminConsole` is NOT a module (T4.1: the management tile never counts as an app), so a host that
 * hands the ONE console table to both derivations gets its `/org/…` pages from `declarations.pages`
 * rather than a phantom module with no nav and an absent standing.
 */
function currentModule(
  pathname: string,
  routes: SidebarInputs['routes'],
): SidebarInputs['routes'][number] | undefined {
  let best: SidebarInputs['routes'][number] | undefined;
  let bestLength = 0;
  for (const route of routes) {
    if (route.adminConsole === true) continue;
    const path = normalizePath(route.path);
    if (path === undefined || !isUnder(pathname, path)) continue;
    if (best === undefined || path.length > bestLength) {
      best = route;
      bestLength = path.length;
    }
  }
  return best;
}

/** The home app's ORG standing — the one non-permission input — read off the row, never the verdict. */
type HomeAppStanding = 'unreported' | 'absent' | 'off' | 'setting-up' | 'on';

function homeAppStanding(standings: ModuleStandings | null, slug: string): HomeAppStanding {
  if (standings === null) return 'unreported';
  const standing = standings[slug];
  if (standing === undefined || !standing.known) return 'absent';
  if (standing.activationState === 'off') return 'off';
  if (standing.activationState === 'setting-up') return 'setting-up';
  if (standing.activationState !== 'on' && !standing.orgEnabled) return 'off';
  return 'on';
}

function moduleLabel(routes: SidebarInputs['routes'], slug: string): string {
  const route = routes.find((candidate) => candidate.slug === slug);
  const label = (route?.label ?? '').trim();
  return label.length > 0 ? label : titleizeSlug(slug);
}

/** A row's href and origin-ness, or `null` when its destination has no address in this build. */
function resolveHref(
  destination: SidebarDestination,
  inputs: SidebarInputs,
): { href: string; external: boolean } | null {
  switch (destination.kind) {
    case 'page':
    case 'org':
      return { href: destination.path, external: false };
    case 'module': {
      const route = inputs.routes.find((candidate) => candidate.slug === destination.slug);
      if (route === undefined) return null;
      const resolved = moduleHref(route, inputs.origin);
      const subpath = destination.subpath ?? '';
      if (resolved !== null) {
        return resolved.kind === 'in-app'
          ? { href: `${resolved.href}${subpath}`, external: false }
          : { href: `${resolved.href.replace(/\/+$/, '')}${subpath}`, external: true };
      }
      // Unmounted here but configured elsewhere: cross-origin, the operator-set URL plus the
      // subpath verbatim — a deterministic composition, never a guessed address.
      const configured = (route.href ?? '').trim();
      if (configured.length === 0) return null;
      return { href: `${configured.replace(/\/+$/, '')}${subpath}`, external: true };
    }
  }
}

/** The module a destination's page belongs to — the "home app" whose standing greys the row. */
function homeApp(destination: SidebarDestination, ownerSlug: string | null): string | null {
  if (destination.kind === 'module') return destination.slug;
  if (destination.kind === 'page') return ownerSlug;
  return null;
}

/**
 * Resolve ONE declared row under the rule in the header. `null` means omit it — a destination with
 * no address here, an `absent` home app, a string the caller lacks with nothing to ask for, or a row
 * whose page its module may withhold (`served`, OM-99) where it would be a request or statement row.
 *
 * `ownerSlug` is the module whose nav this row was declared in (its `page` rows' home app); `null`
 * for a non-module page's declaration, whose `page` rows have no home app. `drawnIn` is the module
 * whose sidebar the row is drawn in — its owner's, except for a BORROWED row, drawn in the
 * borrower's frame.
 */
function resolveRow<Icon>(
  declaration: NavRowDeclaration<Icon>,
  ownerSlug: string | null,
  inputs: SidebarInputs<Icon>,
  badge?: number,
  drawnIn: string | null = ownerSlug,
): SidebarRow<Icon> | null {
  const { permissions, standings } = inputs;
  const resolved = resolveHref(declaration.destination, inputs);
  // A destination with no address in this build is not a row: an unmounted module has no
  // in-console destination at all, and a greyed row leading nowhere is the inert control this
  // contract bans.
  if (resolved === null) return null;
  const home = homeApp(declaration.destination, ownerSlug);
  const base = {
    id: declaration.id,
    label: declaration.label,
    ...(declaration.icon === undefined ? {} : { icon: declaration.icon }),
    requires: declaration.requires,
    // Provisional; `markActive` keeps exactly ONE row active per sidebar — see it.
    active: false,
    ...(badge === undefined ? {} : { badge }),
  };
  // A module-supplied count rides the LIVE row only (OM-30): a request or statement row is a door or a
  // sentence, and a number beside it would be a claim about a page the person cannot open.
  const live = (): SidebarRow<Icon> => ({
    ...base,
    ...(declaration.countBadge === undefined ? {} : { countBadge: declaration.countBadge }),
    // Whether the page is served rides the LIVE row only too (OM-99): it is the page's question.
    ...(declaration.served === undefined ? {} : { served: declaration.served }),
    href: resolved.href,
    external: resolved.external,
    state: 'link',
    testId: `menu-${declaration.id}`,
    ...(declaration.alsoActiveUnder === undefined
      ? {}
      : { alsoActiveUnder: declaration.alsoActiveUnder }),
  });

  // Nothing reported ⇒ every declared row is a plain link — the tile posture, mirrored.
  if (permissions === null) return live();

  const standing = home === null ? 'on' : homeAppStanding(standings, home);
  if (standing === 'absent') return null;
  if (standing === 'off' || standing === 'setting-up') {
    // A row whose page its module may withhold (`served`, OM-99) is never a statement or a request
    // row: where it would be either, it is OMITTED and its hook never asked, so a page kept dark is
    // named nowhere, not even as a door or a sentence about access.
    if (declaration.served !== undefined) return null;
    // The home app is a closed door — its reason is the row's status line, and there is NO request
    // affordance: the string is not what is in the way.
    const label = moduleLabel(inputs.routes, home as string);
    return {
      ...base,
      href: null,
      external: false,
      state: 'statement',
      reason: standing === 'off' ? `${label} is off` : `${label} is setting up`,
      testId: `menu-link-${declaration.id}`,
    };
  }

  const request = (module: string): SidebarRow<Icon> => ({
    ...base,
    href: null,
    external: false,
    state: 'request',
    reason: SIDEBAR_NO_ACCESS_LINE,
    requestModule: module,
    testId: `menu-link-${declaration.id}`,
  });

  // A module's OWN row, drawn in that module's sidebar for a person who does not hold it, is its
  // request row BEFORE the string is read (OM-32): its page sits behind the module's gate, so a
  // string the person holds anyway — one every admin of the class holds — made a live row that led
  // onto the lock. A CROSS-module row (a module's row into ANOTHER module's page, R8) and a BORROWED
  // row (drawn in the borrower's frame, OM-18) keep the string rule below, and a not-reported
  // standing stays permissive.
  if (home !== null && home === drawnIn && standings !== null && standings[home]?.held !== true) {
    // Never a request row either (OM-99, above).
    if (declaration.served !== undefined) return null;
    return request(home);
  }

  const held =
    declaration.requires.trim().length === 0 || permissions.includes(declaration.requires);
  if (held) return live();

  // The string is missing. If it belongs to a module the person does NOT hold, the honest door is
  // a request for a role in that module. If they hold the module and still lack the string — a tier
  // they do not have, and a request is not a tier change — or the page is no module's, there is
  // nothing to do from the row, so there is no row (OM-29: a locked "You do not have access" row
  // was the inert control that rule removes).
  const holdsHome = home !== null && standings?.[home]?.held === true;
  if (home !== null && !holdsHome) {
    // Never a request row either (OM-99, above).
    if (declaration.served !== undefined) return null;
    return request(home);
  }
  return null;
}

type RouteRow = SidebarInputs['routes'][number];

/** What a borrow references: the declared row, and the module whose nav declares it (`null` for an Organization row). */
interface BorrowedReference<Icon> {
  declaration: NavRowDeclaration<Icon>;
  ownerSlug: string | null;
  /** The referenced module's route — its reserved pages are read off its path. */
  route?: RouteRow;
}

/**
 * The row a borrow references, or `undefined` — the borrowed row is OMITTED — when the referenced
 * module is not MOUNTED here (checked FIRST: a host lists a nav whatever the mount, and a `page`-kind
 * row would resolve to its path regardless), or is the `adminConsole` row, or the nav or the row id
 * is unknown. Only a module's OWN declared row can be referenced — never another borrow, and never a
 * LINK into a THIRD module (a `module` destination naming another slug): a page a module does not
 * put in its own sidebar is not a page it offers, and a door is not the page behind it — borrowing
 * one would frame the whole third module, its reserved pages included, under the borrower.
 */
function borrowedReference<Icon>(
  borrow: BorrowedPageRef,
  inputs: SidebarInputs<Icon>,
): BorrowedReference<Icon> | undefined {
  const { declarations, routes } = inputs;
  if ('organization' in borrow) {
    const declaration = declarations.organization.find((row) => row.id === borrow.organization);
    return declaration === undefined ? undefined : { declaration, ownerSlug: null };
  }
  const route = routes.find((candidate) => candidate.slug === borrow.module);
  if (route === undefined || route.adminConsole === true || !isMountedInThisBuild(route)) {
    return undefined;
  }
  const nav = declarations.modules.find((candidate) => candidate.slug === borrow.module);
  const declaration = nav?.sections
    .flatMap((section) => section.rows)
    .find((row): row is NavRowDeclaration<Icon> => !isBorrowedRow(row) && row.id === borrow.row);
  if (
    declaration === undefined ||
    (declaration.destination.kind === 'module' && declaration.destination.slug !== borrow.module)
  ) {
    return undefined;
  }
  return { declaration, ownerSlug: borrow.module, route };
}

/**
 * The IN-APP path a borrowed page sits at, or `undefined` when its destination has no address here
 * or would resolve CROSS-ORIGIN: a borrowed row is embed-or-nothing — a page on another origin
 * cannot wear this frame, and a module that wants a link there declares an ordinary `module` row.
 */
function borrowedPath<Icon>(
  reference: BorrowedReference<Icon>,
  inputs: SidebarInputs<Icon>,
): string | undefined {
  const resolved = resolveHref(reference.declaration.destination, inputs);
  if (resolved === null || resolved.external) return undefined;
  return normalizePath(resolved.href.split(/[?#]/, 1)[0]);
}

/**
 * Resolve a BORROWED row: as the referenced declaration resolves in its own frame — `resolveRow` over
 * its destination and its string, the referenced module as the home app — then re-labelled with the
 * borrower's id, label and icon, and marked `borrowed`. `null` means omit it: the reference resolves
 * to nothing, the referenced module is not mounted here, or its page is cross-origin.
 */
function resolveBorrowedRow<Icon>(
  declaration: BorrowedRowDeclaration<Icon>,
  borrower: string,
  inputs: SidebarInputs<Icon>,
): SidebarRow<Icon> | null {
  const reference = borrowedReference(declaration.borrow, inputs);
  if (reference === undefined || borrowedPath(reference, inputs) === undefined) return null;
  const resolved = resolveRow(
    reference.declaration,
    reference.ownerSlug,
    inputs,
    undefined,
    borrower,
  );
  if (resolved === null) return null;
  const icon = declaration.icon ?? resolved.icon;
  return {
    ...resolved,
    id: declaration.id,
    label: declaration.label ?? resolved.label,
    ...(icon === undefined ? {} : { icon }),
    testId: resolved.state === 'link' ? `menu-${declaration.id}` : `menu-link-${declaration.id}`,
    borrowed: true,
  };
}

function resolveSection<Icon>(
  section: NavSectionDeclaration<Icon>,
  ownerSlug: string | null,
  inputs: SidebarInputs<Icon>,
): SidebarSection<Icon> {
  const rows: SidebarRow<Icon>[] = [];
  for (const declaration of section.rows) {
    // A borrow belongs to a MODULE's nav: a non-module page has no frame to lend, so a borrow
    // declared in its sections is omitted (the omission law — the host's rail names it).
    const row = isBorrowedRow(declaration)
      ? ownerSlug === null
        ? null
        : resolveBorrowedRow(declaration, ownerSlug, inputs)
      : resolveRow(declaration, ownerSlug, inputs);
    if (row !== null) rows.push(row);
  }
  return { id: section.id, label: section.label, rows };
}

function organizationSection<Icon>(inputs: SidebarInputs<Icon>): SidebarSection<Icon> {
  const { declarations } = inputs;
  const rows: SidebarRow<Icon>[] = [];
  for (const declaration of declarations.organization) {
    const row = resolveRow(declaration, null, inputs);
    if (row !== null) rows.push(row);
  }
  return { id: 'organization', label: declarations.organizationLabel ?? 'Organization', rows };
}

/**
 * The per-module Team row the SHELL declares for every mounted module (T6.7) — path
 * `<module path>/team`, `requires` = that module's approval string: an Owner's row, and ABSENT for
 * anyone without the string (Org Machinery OM-29 — `moduleSections`). A module adds nothing to get it.
 * It reads "Admin Team" in every app, as the Admin Console's own team page does (OM-100).
 */
export function teamRowFor<Icon>(
  route: ModuleRoute,
  icon: Icon | undefined,
  label: string = TEAM_COPY.adminTeam,
): NavRowDeclaration<Icon> {
  const path = normalizePath(route.path) ?? `/${route.slug}`;
  return {
    id: `${route.slug}-team`,
    label,
    ...(icon === undefined ? {} : { icon }),
    destination: { kind: 'page', path: `${path}/team` },
    requires: approveAccessRequestsPermission(route.slug),
  };
}

/**
 * Mark exactly ONE row active — the longest same-origin href the pathname sits under — and answer
 * its label as the page title (the family's own name when no row matches).
 *
 * ⚠ THE FAMILY ROOT MATCHES EXACTLY, every other row by segment prefix. A module's landing row
 * (`/board`) would otherwise be "active" on every page beneath it, and the bar would name the
 * landing page from inside a subpage the sidebar has no row for. Longest match, not first match —
 * the same rule the shell and `currentModuleFor` already resolve with. A row's `alsoActiveUnder`
 * paths match by segment prefix too, each competing at its own length.
 */
function markActive<Icon>(
  sections: readonly SidebarSection<Icon>[],
  pathname: string,
  rootPath: string | null,
  fallback: string,
): string {
  let best: SidebarRow<Icon> | undefined;
  let bestLength = -1;
  for (const section of sections) {
    for (const row of section.rows) {
      if (row.href === null || row.external) continue;
      const length = matchedLength(row.href, row.alsoActiveUnder, pathname, rootPath);
      if (length > bestLength) {
        best = row;
        bestLength = length;
      }
    }
  }
  if (best !== undefined) best.active = true;
  return best?.label ?? fallback;
}

/**
 * The length of the longest path a row claims the pathname by — its own href (the family root only
 * exactly) or one of the pages it owns — or `-1` when it claims nothing.
 */
function matchedLength(
  href: string,
  alsoActiveUnder: readonly string[] | undefined,
  pathname: string,
  rootPath: string | null,
): number {
  let length = (href === rootPath ? pathname === href : isUnder(pathname, href)) ? href.length : -1;
  for (const path of alsoActiveUnder ?? []) {
    if (path.length > length && isUnder(pathname, path)) length = path.length;
  }
  return length;
}

/**
 * A module's frame — its declared sections (each row under the string rule) + the shell's Team row
 * (joining `teamSection`, or the first section, or a section of its own) + the Organization section
 * unless the module opts out — with every empty section dropped: every row in it was omitted
 * (unmounted destinations, an absent home app), and a heading over nothing is the phantom the drawer
 * must not show.
 *
 * `organizationAlways` is the frame on an ORGANIZATION page (the fourth branch): that page's own row
 * lives in the Organization section, so a module's opt-out — a statement about its own pages — does
 * not remove the section there.
 *
 * THE ORGANIZATION DEDUPE (a page appears once per sidebar): an Organization row this module BORROWS
 * into its own section is omitted from the Organization section in ITS frame — the borrowed row is
 * the module's deliberate placement and wins — keyed by the REFERENCE's row id, never by href (a
 * greyed row's href is `null`). The deduped section is a COPY, never the shared one; the flagged-page
 * branch therefore tests the section by id.
 *
 * THE ADMIN GROUP (Org Machinery OM-69): where the module declares an `adminCaption`, the Team row
 * joins the Organization section FIRST instead of a module section, the frame carries that section
 * whatever the module's opt-out says, and the section — a copy, the shared one never touched —
 * carries the caption. Every frame of the module is built here (its own
 * pages, an Organization page, a borrowed page, the settings page opened from it), so the caption
 * appears in exactly that module's frame and nowhere else.
 */
function moduleSections<Icon>(
  module: RouteRow,
  inputs: SidebarInputs<Icon>,
  organization: SidebarSection<Icon>,
  organizationAlways = false,
): SidebarSection<Icon>[] {
  const { routes, declarations, permissions } = inputs;
  const nav = declarations.modules.find((candidate) => candidate.slug === module.slug);
  const adminCaption = (nav?.adminCaption ?? '').trim();
  const sections: SidebarSection<Icon>[] = (nav?.sections ?? []).map((section) =>
    resolveSection(section, module.slug, inputs),
  );
  // THE TEAM ROW RENDERS ONLY FOR A HOLDER OF ITS STRING (Org Machinery OM-29, Jonathan 2026-09-28:
  // a control the caller cannot use is absent). The row rule already drops it for a Member of the
  // module; this check also drops the Owner-only REQUEST row it would give someone who does not hold
  // the module at all — the module's own rows already carry that door, and a holder of the string
  // who does not hold the module is not given one either (OM-32 would make the row a request). A
  // not-reported standing keeps the tile posture (every declared row a plain link).
  const teamDeclaration = teamRowFor(module, declarations.teamIcon, inputs.teamLabel);
  const holdsModule = inputs.standings === null || inputs.standings[module.slug]?.held === true;
  const team =
    permissions === null || (holdsModule && permissions.includes(teamDeclaration.requires))
      ? resolveRow(teamDeclaration, module.slug, inputs, inputs.pendingByModule?.[module.slug])
      : null;
  // With an admin caption the Team row opens the admin group below instead (OM-69).
  if (team !== null && adminCaption.length === 0) {
    const home =
      sections.find((section) => section.id === nav?.teamSection) ??
      sections[0] ??
      (() => {
        const own: SidebarSection<Icon> = {
          id: module.slug,
          label: moduleLabel(routes, module.slug),
          rows: [],
        };
        sections.push(own);
        return own;
      })();
    home.rows.push(team);
  }
  const borrowedOrganizationRows = new Set(
    (nav?.sections ?? [])
      .flatMap((section) => section.rows)
      .flatMap((row) =>
        isBorrowedRow(row) && 'organization' in row.borrow ? [row.borrow.organization] : [],
      ),
  );
  const organizationHere =
    borrowedOrganizationRows.size === 0
      ? organization
      : {
          ...organization,
          rows: organization.rows.filter((row) => !borrowedOrganizationRows.has(row.id)),
        };
  if (adminCaption.length > 0) {
    // The ADMIN group: Team first, then the Organization rows this page carries, under the module's
    // own words. A declared caption makes the frame CARRY the Organization section whatever
    // `includeOrganizationSection` says, as it takes the Team row whatever `teamSection` says: the
    // caption is drawn over that section, and the flagged-page branch reads the section's id as the
    // section being there, so the settings page cold-loaded in this frame keeps its Organization
    // rows. Dropped like any empty section when it holds no row.
    const rows = [...(team === null ? [] : [team]), ...organizationHere.rows];
    if (rows.length > 0) sections.push({ ...organizationHere, rows, caption: adminCaption });
  } else if (
    (organizationAlways || nav?.includeOrganizationSection !== false) &&
    organizationHere.rows.length > 0
  ) {
    sections.push(organizationHere);
  }
  return sections.filter((section) => section.rows.length > 0);
}

/** Whether a declared row's destination is the module's own ROOT — the row that opens the module. */
function isModuleRoot(destination: SidebarDestination, module: RouteRow): boolean {
  if (destination.kind === 'module') {
    return (
      destination.slug === module.slug &&
      (destination.subpath ?? '').trim().replace(/\/+$/, '').length === 0
    );
  }
  if (destination.kind === 'page') {
    const path = normalizePath(module.path);
    return path !== undefined && normalizePath(destination.path) === path;
  }
  return false;
}

/**
 * Whether this person can OPEN a module: they HOLD it (a reported standing's `held` — the same
 * field the row rule reads to choose request-vs-absent; a not-reported standing is permissive)
 * and its landing row resolves to a `link` under the row rule — the same standing and permission
 * inputs the module branch resolves every row with, never the tile verdict. The landing row is the
 * module's declared root row; a module that declares no nav has the shell's Team row as its only
 * row, and that row stands in — an Owner of a nav-less module keeps a live way back, a Member gets
 * no frame of greyed rows. The `held` half matters where the root row requires nothing: that row is
 * a plain link on an empty effective set, and a frame of live rows into a module the person does
 * not hold would be a door onto a gate.
 */
function moduleOpenable<Icon>(module: RouteRow, inputs: SidebarInputs<Icon>): boolean {
  const { standings } = inputs;
  if (standings !== null && standings[module.slug]?.held !== true) return false;
  const nav = inputs.declarations.modules.find((candidate) => candidate.slug === module.slug);
  // A borrowed row has no destination of its own and is never a module's landing row.
  const landing = nav?.sections
    .flatMap((section) => section.rows)
    .find(
      (row): row is NavRowDeclaration<Icon> =>
        !isBorrowedRow(row) && isModuleRoot(row.destination, module),
    );
  const declaration = landing ?? teamRowFor(module, inputs.declarations.teamIcon, inputs.teamLabel);
  return resolveRow(declaration, module.slug, inputs)?.state === 'link';
}

/**
 * The Organization row whose page `pathname` is — the longest `org` destination it sits under, by
 * segment — or `undefined` off every Organization page. A row a host did not declare (a dark class
 * page) claims nothing, so its address stays the not-found surface's.
 */
function organizationRowFor<Icon>(
  pathname: string,
  declarations: SidebarDeclarations<Icon>,
): NavRowDeclaration<Icon> | undefined {
  let best: NavRowDeclaration<Icon> | undefined;
  let bestLength = 0;
  for (const row of declarations.organization) {
    if (row.destination.kind !== 'org') continue;
    const path = normalizePath(row.destination.path);
    if (path === undefined || !isUnder(pathname, path)) continue;
    if (best === undefined || path.length > bestLength) {
      best = row;
      bestLength = path.length;
    }
  }
  return best;
}

/**
 * ONE frame candidate under the guard — the slug names a MOUNTED module (a path and an address here,
 * never the `adminConsole` row) that this person can OPEN — else `undefined`. The two-step rule and
 * the borrowed-frame step share it; neither re-states it.
 */
function frameCandidate<Icon>(
  candidate: string | null | undefined,
  inputs: SidebarInputs<Icon>,
): RouteRow | undefined {
  const slug = (candidate ?? '').trim();
  if (slug.length === 0) return undefined;
  const route = inputs.routes.find((route) => route.slug === slug);
  if (route === undefined || route.adminConsole === true || !isMountedInThisBuild(route)) {
    return undefined;
  }
  return moduleOpenable(route, inputs) ? route : undefined;
}

/**
 * The module whose frame the page wears, or `undefined` when none applies: the host's `frameModule`
 * (the hint) first, then its `frameFallback` (the class default), each under the guard. A stale
 * hint therefore falls to the class default, and only then to nothing.
 */
function frameModuleFor<Icon>(inputs: SidebarInputs<Icon>): RouteRow | undefined {
  for (const candidate of [inputs.frameModule, inputs.frameFallback]) {
    const route = frameCandidate(candidate, inputs);
    if (route !== undefined) return route;
  }
  return undefined;
}

/** The shell's own pages under a module path — never borrowed, whatever row a borrow names. */
const RESERVED_MODULE_PAGES = ['team', 'sign-in'] as const;

/**
 * Whether `nav` declares a borrowed row whose page COVERS `pathname` — the page and every address
 * beneath it by segment, exactly as a row matches — the referenced module's reserved pages excluded.
 */
function navBorrows<Icon>(
  nav: ModuleNav<Icon>,
  pathname: string,
  inputs: SidebarInputs<Icon>,
): boolean {
  for (const section of nav.sections) {
    for (const row of section.rows) {
      if (!isBorrowedRow(row)) continue;
      const reference = borrowedReference(row.borrow, inputs);
      if (reference === undefined) continue;
      const path = borrowedPath(reference, inputs);
      if (path === undefined || !isUnder(pathname, path)) continue;
      const modulePath =
        reference.route === undefined ? undefined : normalizePath(reference.route.path);
      if (
        modulePath !== undefined &&
        RESERVED_MODULE_PAGES.some((page) => isUnder(pathname, `${modulePath}/${page}`))
      ) {
        continue;
      }
      return true;
    }
  }
  return false;
}

/**
 * THE BORROWED-FRAME STEP — ahead of every other branch. Candidates, in order: the frame the
 * navigation CARRIED (`borrowedFrom` — a slug is tried alone: a row followed in a frame that does
 * not borrow the page was a LINK, and the destination's own frame is the right answer; `null` STOPS
 * the search — a module switch), then, only when nothing was carried, the hint (`frameModule`).
 * NEVER `frameFallback`: an Organization page or the settings page has no frame of its own and the
 * class default stands in; a borrowed page HAS one — its module's — and that is the direct-load
 * fallback. A candidate frames iff it passes the guard AND its nav declares a borrowed row covering
 * the pathname.
 */
function borrowedFrameFor<Icon>(
  pathname: string,
  inputs: SidebarInputs<Icon>,
): RouteRow | undefined {
  const { borrowedFrom, frameModule, declarations } = inputs;
  if (borrowedFrom === null) return undefined;
  const carried = (borrowedFrom ?? '').trim();
  const candidates = carried.length > 0 ? [carried] : [frameModule];
  for (const candidate of candidates) {
    const route = frameCandidate(candidate, inputs);
    if (route === undefined) continue;
    const nav = declarations.modules.find((candidate) => candidate.slug === route.slug);
    if (nav !== undefined && navBorrows(nav, pathname, inputs)) return route;
  }
  return undefined;
}

/**
 * Whether a flagged page's ORIGIN — the page the gear was pressed on, re-entered — gives this
 * person a place to stand (the OM-28 review fold). Before OM-28 only the gear wrote the origin, on
 * a page the person was standing on; the ADDRESS can say anything — a link a Global Admin copied
 * from `/org`, a hand-edited `frame`, a bookmark older than the person's access — so an origin
 * counts only when:
 *
 *   * its re-entry draws something — the Admin Console family of someone who holds none of its
 *     pages draws nothing, and a page that names a place it cannot show is no place;
 *   * a frame the address carried is the frame the re-entry WORE — a frame that does not frame the
 *     origin (a hand-edited one, or one a later build stopped borrowing) is not a place either;
 *   * the module whose frame it wears is one this person HOLDS (a not-reported standing is
 *     permissive, the tile posture) — a module page renders its frame for anyone standing on it,
 *     and an origin inside a module the person does not hold is a gate, not a place.
 *
 * Anything else falls to the cold-load rule, as a bare address does, and the composed page takes
 * its module from the SAME sidebar's frame (the hosts hand `Sidebar.frame` to the page), so the
 * sidebar, the bar and the page's module section can never name two modules.
 */
function originGivesAPlace<Icon>(
  origin: Sidebar<Icon>,
  carried: string,
  inputs: SidebarInputs<Icon>,
): boolean {
  if (origin.sections.length === 0) return false;
  if (carried.length > 0 && origin.frame !== carried) return false;
  if (origin.frame === undefined || inputs.standings === null) return true;
  return inputs.standings[origin.frame]?.held === true;
}

/**
 * The rule: the sidebar for a pathname.
 *
 *   * a page BORROWED into another module's frame — the frame the navigation carried, else the hint,
 *     names a mounted, openable module whose nav borrows the page → THAT module's frame, exactly as
 *     its own pages render it, the borrowed row active and its label the title;
 *   * a MOUNTED MODULE's page → its declared sections (each row under the string rule) + the
 *     shell's Team row (joining `teamSection`, or the first section, or a section of its own) +
 *     the Organization section unless the module opts out;
 *   * a declared NON-MODULE page → its own sections (+ the Organization section unless it opts
 *     out); flagged `moduleFrame`, the frame below comes FIRST and its own sections trail it,
 *     its own word the title;
 *   * an ORGANIZATION-SECTION page, when the host's `frameModule` (the hint) — else its
 *     `frameFallback` (the class default) — names a mounted module this person can open → THAT
 *     module's frame, exactly as above (the Organization section always), with the page's own row
 *     active and its label the title;
 *   * anything else → the Organization section alone, the honest minimum.
 *
 * The title is the ACTIVE row's label when one is active, else the family's own name — so an app's
 * landing page names that page once ("Donations"), never "Donations › Donations".
 *
 * And the CAPTIONS are the frame's (Org Machinery OM-27): drawn in the frame of a module that
 * declares more than one section, hidden everywhere else — see {@link captionsFor}. A module's
 * `adminCaption` (OM-69) is not that answer: it rides on its own frame's Organization section as the
 * section's `caption`, drawn whatever the answer or a host's override says.
 */
export function sidebarFor<Icon = unknown>(inputs: SidebarInputs<Icon>): Sidebar<Icon> {
  const sidebar = resolveSidebar(inputs);
  return { ...sidebar, captions: captionsFor(sidebar.frame, inputs.declarations) };
}

/**
 * Whether a sidebar draws its section captions — decided by the module whose FRAME it wears (Org
 * Machinery OM-27; Jonathan 2026-09-28 scoping his 2026-09-12 ruling to the one five-page module he
 * had been looking at: "it makes no sense to have 3 category headers for 5 total pages - but i dind't
 * mean that should be applied broadly across every module" — §OM-14's 2026-09-28 amendment quotes
 * him in full). A module that DECLARES more than one section draws them — every caption in its frame,
 * the Organization section's included, with the group gap they bring back — whoever is looking, so
 * the frame looks the same on every page that wears it (its own, an Organization page, a borrowed
 * page, the settings page opened from it). A one-section module, the Admin Console family and any
 * unframed page keep the captionless, gapless list OM-21 and OM-23 made. DECLARED, not drawn: a
 * person who can open only one of a module's sections still sees that module's groups named.
 */
function captionsFor<Icon>(
  frame: string | undefined,
  declarations: SidebarDeclarations<Icon>,
): SidebarCaptions {
  if (frame === undefined) return 'hidden';
  const nav = declarations.modules.find((candidate) => candidate.slug === frame);
  return (nav?.sections.length ?? 0) > 1 ? 'shown' : 'hidden';
}

function resolveSidebar<Icon>(inputs: SidebarInputs<Icon>): Sidebar<Icon> {
  const { pathname, routes, declarations } = inputs;
  const organization = organizationSection(inputs);

  // A page BORROWED into another module's frame — BEFORE the module branch, because the borrower's
  // frame wins over the destination's own wherever the navigation (or, inside the borrowed subtree,
  // the hint) names it. The borrowed row is the longest match on every address beneath its page, so
  // it is the one active row and its word the title; the borrower's root matches exactly, as on its
  // own pages.
  const borrower = borrowedFrameFor(pathname, inputs);
  if (borrower !== undefined) {
    const sections = moduleSections(borrower, inputs, organization);
    const label = moduleLabel(routes, borrower.slug);
    const title = markActive(sections, pathname, normalizePath(borrower.path) ?? null, label);
    // The bar names the borrower — the page reads as one of its own (OM-26) — except on an
    // Organization page, which reads "Organization" in every frame (the 2026-09-29 ruling).
    const app =
      organizationRowFor(pathname, declarations) === undefined ? label : organization.label;
    return { title, app, sections, frame: borrower.slug };
  }

  const module = currentModule(pathname, routes);
  if (module !== undefined) {
    const sections = moduleSections(module, inputs, organization);
    const app = moduleLabel(routes, module.slug);
    const title = markActive(sections, pathname, normalizePath(module.path) ?? null, app);
    return { title, app, sections, frame: module.slug };
  }

  const page = (declarations.pages ?? []).find((candidate) =>
    candidate.prefix === true ? isUnder(pathname, candidate.path) : candidate.path === pathname,
  );
  if (page !== undefined) {
    const own = page.sections
      .map((section) => resolveSection(section, null, inputs))
      .filter((section) => section.rows.length > 0);
    // A page flagged `moduleFrame` opened FROM an address wears that address's sidebar — exactly
    // as it renders there, no row lit — with its own sections after it and its own title. The
    // origin's sidebar is the whole answer: nothing is appended to it.
    const originPath = normalizePath(
      page.moduleFrame === true ? (inputs.framePath ?? undefined) : undefined,
    );
    if (originPath !== undefined && originPath !== normalizePath(page.path)) {
      // The carried frame (`borrowedFrom` — the gear names it beside the address) rides into the
      // re-entry, so a page borrowed into a module's frame re-enters that frame exactly, a stale
      // hint notwithstanding — and where the origin is an Organization page the carried module
      // borrows nothing from, the same slug stands in for the hint.
      const carried = (inputs.borrowedFrom ?? '').trim();
      const origin = sidebarFor({
        ...inputs,
        pathname: originPath,
        framePath: null,
        ...(carried.length > 0 ? { frameModule: carried } : {}),
      });
      // The origin rides the ADDRESS (Org Machinery OM-28), which anyone can copy, bookmark or edit,
      // so it counts only where it gives THIS person a place to stand (the OM-28 review fold);
      // otherwise the page falls to the cold-load rule below, exactly as a bare address does.
      if (originGivesAPlace(origin, carried, inputs)) {
        for (const section of origin.sections) {
          for (const row of section.rows) row.active = false;
        }
        const sections = [...origin.sections, ...own];
        const title = markActive(
          sections,
          pathname,
          page.prefix === true ? page.path : null,
          page.title,
        );
        // The bar names the module whose frame the page wears — the module it was opened from ("a)
        // Board", the 2026-09-29 ruling) — else the app of the page it was opened from ("Admin
        // Console", "Organization"), else the page's own word (OM-26).
        const app =
          origin.frame === undefined ? (origin.app ?? page.app) : moduleLabel(routes, origin.frame);
        return {
          title,
          ...(app === undefined ? {} : { app }),
          sections,
          ...(origin.frame === undefined ? {} : { frame: origin.frame }),
        };
      }
    }
    // Otherwise a flagged page wears the frame FIRST — the module's own sections, its Team row and
    // the Organization section, under the same guard as an Organization page — and its own
    // sections trail it; the title stays the page's own word. The Organization section renders
    // exactly once: inside the frame when the module carries it (by id — the frame may carry a
    // deduped copy), else after the page's own sections when the page asks for it.
    const frame = page.moduleFrame === true ? frameModuleFor(inputs) : undefined;
    const framed = frame === undefined ? [] : moduleSections(frame, inputs, organization);
    const sections = [...framed, ...own];
    if (
      !framed.some((section) => section.id === organization.id) &&
      page.includeOrganizationSection !== false &&
      organization.rows.length > 0
    ) {
      sections.push(organization);
    }
    const title = markActive(
      sections,
      pathname,
      page.prefix === true ? page.path : null,
      page.title,
    );
    const app = frame === undefined ? page.app : moduleLabel(routes, frame.slug);
    return {
      title,
      ...(app === undefined ? {} : { app }),
      sections,
      ...(frame === undefined ? {} : { frame: frame.slug }),
    };
  }

  // An Organization page opened from a module keeps that module's frame — the page's own row active
  // and its label the title, never the module's landing page; the frame's module root matches
  // exactly, as on the module's own pages. (A module that BORROWS the row into its own section
  // framed it in the step above; here the frame module borrows nothing there.)
  const organizationRow = organizationRowFor(pathname, declarations);
  if (organizationRow !== undefined) {
    const frame = frameModuleFor(inputs);
    if (frame !== undefined) {
      const sections = moduleSections(frame, inputs, organization, true);
      const title = markActive(
        sections,
        pathname,
        normalizePath(frame.path) ?? null,
        organizationRow.label,
      );
      // The bar reads "Organization" in the module's frame ("b) Organization", the 2026-09-29
      // ruling): the page is the organization's, not the module's.
      return { title, app: organization.label, sections, frame: frame.slug };
    }
  }

  // The floor. An Organization page no module frames is in the Organization's own part of the
  // console, so the bar names it; anything else here (the not-found surface) is no app's.
  const sections = organization.rows.length > 0 ? [organization] : [];
  const title = markActive(sections, pathname, null, '');
  return {
    title,
    ...(organizationRow === undefined ? {} : { app: organization.label }),
    sections,
  };
}

// ── The shell's TRAILING row — Settings, the LAST row of the list (Org Machinery OM-22) ───────

/** The row's id — its testid is `menu-settings-page`; the retired `menu-settings` is never re-used. */
export const SETTINGS_ROW_ID = 'settings-page';
/** The one word on the row, the page and the breadcrumb. */
export const SETTINGS_ROW_LABEL = 'Settings';

export interface SettingsRowInputs<Icon = unknown> {
  pathname: string;
  /** The host's route constant for the settings page — handed in, never a literal here. */
  href: string;
  /** The gear glyph — the same mark the cluster's gear wears, so the two doors read as one place. */
  icon?: Icon;
}

/**
 * The ONE Settings row, rendered as the LAST ROW of every sidebar and drawer — shell chrome on every
 * page that wears the console frame ("the same on every app and the Admin Console"). It is
 * deliberately OUTSIDE `sidebarFor`: not a declaration a module or a host makes, not a section, and
 * not a row inside any declared section — a module can neither add nor remove it, and the
 * Organization section's absence on an Admin Console page does not touch it. A plain link
 * (`requires: ''` — any admin may open the page; its sections gate themselves), active iff the
 * pathname IS the settings page. The exactly-one-active-row law holds with it counted: on the
 * settings page `sidebarFor` lights no row (the flagged-page case), so this is the one. Its meaning
 * is the gear's: the host's navigate carries the same origin the gear would from the page it was
 * pressed on.
 *
 * WHERE IT RENDERS (Org Machinery OM-22, Jonathan 2026-09-12 — this REVERSES D-CPO-35 (1) and the
 * 2026-09-05 mockup pass item 3, which had put it in a foot group inside the host's footer block):
 * the host hands it to `OrgSidebarNav` as `trailingRow`, and the nav appends it to the LAST drawn
 * group's own `<ul>` — the last row of the list, the same row-to-row spacing as any two rows, no
 * group of its own, no caption, no extra gap, scrolling with the list. Verbatim: "I just want
 * Settings to always be the last page in the left panel" · "I don't want the settings gap to be
 * different than any other page spacing, just need it to always be the last row."
 *
 * ⚠ `OrgSidebarFoot` and its `sidebar-foot` id are RETIRED with that reversal, and neither name is
 * re-used. The account block is identity + sign-out only. Do not re-propose a foot group: the 09-05
 * reason for one (after a long nav the row read as one more Organization row) was heard and
 * overruled — and with captions hidden (OM-21) there is no caption for it to read as sitting under.
 */
export function settingsRowFor<Icon = unknown>(inputs: SettingsRowInputs<Icon>): SidebarRow<Icon> {
  const href = normalizePath(inputs.href) ?? inputs.href;
  return {
    id: SETTINGS_ROW_ID,
    label: SETTINGS_ROW_LABEL,
    ...(inputs.icon === undefined ? {} : { icon: inputs.icon }),
    href,
    external: false,
    state: 'link',
    requires: '',
    active: normalizePath(inputs.pathname) === href,
    testId: `menu-${SETTINGS_ROW_ID}`,
  };
}
